'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export interface Currency {
  id: string;
  name: string;
  flag: string;
  code: string;
  locale: string;
}

export const currencies: Currency[] = [
  {
    id: 'cad',
    name: 'Canada (English/$CAD)',
    flag: '/flags/canada-flag.png',
    code: 'CAD',
    locale: 'en-CA'
  },
  {
    id: 'usd',
    name: 'United States (English/$USD)',
    flag: '/flags/usa-flag.png',
    code: 'USD',
    locale: 'en-US'
  }
];

interface CurrencyContextType {
  selectedCurrency: Currency;
  setCurrency: (currency: Currency) => void;
  currencies: Currency[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(() => {
    // Provide a default currency as fallback
    return currencies[0] || {
      id: 'cad',
      name: 'Canada (English/$CAD)',
      flag: '/flags/canada-flag.png',
      code: 'CAD',
      locale: 'en-CA'
    };
  });

  // Load currency preference from URL params or localStorage on mount
  useEffect(() => {
    const urlCurrency = searchParams.get('currency');
    const savedCurrencyId = localStorage.getItem('pionpower-currency');
    
    let targetCurrencyId = 'cad';
    
    if (urlCurrency && ['CAD', 'USD'].includes(urlCurrency.toUpperCase())) {
      targetCurrencyId = urlCurrency.toLowerCase() === 'usd' ? 'usd' : 'cad';
    } else if (savedCurrencyId) {
      targetCurrencyId = savedCurrencyId;
    }
    
    const targetCurrency = currencies.find(c => c.id === targetCurrencyId);
    if (targetCurrency) {
      setSelectedCurrency(targetCurrency);
    }
  }, [searchParams]);

  const setCurrency = (currency: Currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('pionpower-currency', currency.id);

    /*
     * IMPORTANT
     * ---------
     * Each currency has its own cart in Shopify because line-item presentment prices
     * are fixed when the cart is created (based on buyerIdentity / country context).
     * Clearing the cart cookie forces the backend helper `getOrCreateCart` to
     * create a fresh cart using the new currency.
     */
    try {
      // Expire the cartId cookie immediately
      document.cookie = 'cartId=; Max-Age=0; path=/; SameSite=Lax;';
    } catch (e) {
      console.warn('Failed to clear cart cookie:', e);
    }

    // Also persist as cookie for server-side middleware (30 days)
    try {
      document.cookie = `currency=${currency.id}; Max-Age=${60 * 60 * 24 * 30}; Path=/; SameSite=Lax;`;
    } catch (e) {
      console.warn('Failed to set currency cookie:', e);
    }

    // Preserve the current path (without the old currency prefix) when switching
    const currentPath = window.location.pathname;
    const pathWithoutCurrency = currentPath.replace(/^\/(cad|usd)/, '') || '/';

    // Push the new route – this triggers a server navigation and re-renders layout
    const newPath = `/${currency.id}${pathWithoutCurrency}`;
    router.push(newPath);
  };

  const value = {
    selectedCurrency,
    setCurrency,
    currencies
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
} 